<template>
  <div class="kpt-subscriptions-selector">
    <b-input type="text" icon="search" @input="getFilteredSubscriptions" v-model="searchTerm" :loading="isFetchingSubs" placeholder="Search by subscription product..."></b-input>
    <b-table 
      :data="availableSubscriptions" 
      :loading="isFetchingSubs"
      :scrollable="false"
      checkable 
      :header-checkable="false"
      :is-row-checkable="(row) => isRowCheckable(row)"
      :custom-is-checked="(a, b) => a.id === b.id"
      :checked-rows="checkedSubscriptions"
      :paginated="true"
      :per-page="5"
      :pagination-simple="true"
      @check="subscriptionSelected"
      :current-page="currentPage">
      <b-table-column field="kymetaProductCode" label="Product Code" v-slot="props" sortable>
        {{ props.row.kymetaProductCode }}
      </b-table-column>
      <b-table-column field="displayId" label="Friendly Id" v-slot="props" sortable>
        {{ props.row.displayId }}
      </b-table-column>
      <b-table-column field="accountName" label="Account" v-slot="props" sortable>
        {{ props.row.accountName }}
      </b-table-column>
      <b-table-column field="startDate" label="Start Date" v-slot="props" sortable>
        {{ format(new Date(props.row.startDate), 'yyyy-MM-dd') }}
      </b-table-column>
      <b-table-column field="endDate" label="End Date" v-slot="props" sortable>
        {{ format(new Date(props.row.endDate), 'yyyy-MM-dd') }}
      </b-table-column>
      <b-table-column field="productName" label="Product" v-slot="props" sortable>
        {{ props.row.productName }}
      </b-table-column>
      <b-table-column field="dataPackageName" label="Data Package" v-slot="props" sortable>
        {{ props.row.dataPackageName }}
      </b-table-column>
      <template slot="empty">
        <p v-if="!isFetchingSubs">No available subscriptions found.</p>
        <p v-else>Loading subscriptions...</p>
      </template>
    </b-table>
  </div>
</template>
<script>
import { debounce, orderBy } from 'lodash';
import { format } from 'date-fns';

export default {
  data() {
    return {
      isFirstLoad: true,
      searchTerm: null,
      availableSubscriptions: [],
      checkedSubscriptions: [],
      isFetchingSubs: false,
      currentPage: 1,
      format
    }
  },
  props: {
    accountFilter: String,
    isMultipleSelect: {
      type: Boolean,
      default: false
    },
    preSelected: {
      type: Array,
      default: () => []
    }
  },  
  mounted() {
    this.getFilteredSubscriptions();
  },
  computed: {
    accounts() {
      return this.$store.state.accounts.accounts.map(a => a.id);
    }
  },
  methods: {
    reset() {
      this.checkedSubscriptions = [];
      this.getFilteredSubscriptions();
    },
    // eslint-disable-next-line func-names
    getFilteredSubscriptions: debounce(async function (searchTerm) {
      this.isFetchingSubs = true;
      let subscriptions = [];
      // we must loop this request to account for a total device count over 1000 (search index limitation)
      // TODO: this will be replaced when we implement a solution to address the search index limitation of 1000 records returned
      for (let i = 0; i <= 1000000; i++) {
        let url = `/v2.1/subscriptions?from=${i}&take=1000&sortBy=ProductName&sortDirection=asc&select=Id,Name,KymetaProductCode,DisplayId,AccountId,AccountName,StartDate,EndDate,ProductName,DataPackageName`;
        if (this.accountFilter || searchTerm) {
          url += '&filters=';
          const filters = [];
          if (this.accountFilter) filters.push(`AccountId:eq:${this.accountFilter}`);
          if (searchTerm) filters.push(`Name:eq:${searchTerm}`);
          // append filters to URL
          url += filters.length > 1 ? filters.join('|') : filters.shift();
        }

        // eslint-disable-next-line no-await-in-loop
        const res = await fetch(url);

        if (!res.ok) break;
        // eslint-disable-next-line no-await-in-loop
        const data = await res.json();
        subscriptions = [...subscriptions, ...data.payload];
        i += 999;

        // total number of remotes is less than our count, no need to fetch beyond total count
        if (data.count < i) break;
      }
      this.isFetchingSubs = false;

      // filter to show only entities associated with logged in user accounts(including children)
      const entities = subscriptions.filter(e => this.accounts.includes(e.accountId));
      this.availableSubscriptions = orderBy(entities, ['productName'], ['asc']);
      
      // check for pre-selected items passed to the component
      if (this.isFirstLoad && this.preSelected && !!this.preSelected.length) {
        const preSelectedIds = this.preSelected.map(ps => ps.id);
        const checked = this.availableSubscriptions.filter(as => preSelectedIds.includes(as.id));
        this.checkedSubscriptions = checked;
      }
      
      // check for filter to see if we need to limit results
      if (this.accountFilter) {
        this.availableSubscriptions = this.availableSubscriptions.filter(ar => ar.accountId == this.accountFilter);
      }

      // prevent pre-selected items from impacting selection for future searches
      this.isFirstLoad = false;
    }, 500),
    // eslint-disable-next-line no-unused-vars
    subscriptionSelected(subscriptions, eventItem) {
      this.checkedSubscriptions = subscriptions;
      this.$emit('item-select', subscriptions);
    },
    unselectItem(item) {
      // search for checked row
      const foundItemIndex = this.checkedSubscriptions.findIndex(cr => cr.id == item.id);

      // if the item is found, remove it from the checked array
      if (foundItemIndex != -1) { 
        this.checkedSubscriptions.splice(foundItemIndex, 1);
        this.$emit('item-select', this.checkedSubscriptions);
      }
    },
    isRowCheckable(row) {
      const existsInList = this.checkedSubscriptions.find(r => r.id == row.id) != null;
      if (existsInList) return true;
      return this.isMultipleSelect ? true : this.checkedSubscriptions.length < 1;
    }
  }
}
</script>
<style lang="scss" scoped>
.kpt-subscriptions-selector {
  ::v-deep .table-wrapper {
    overflow: hidden;
  }
}
</style>
